import { consola } from 'consola'
/**
 * Утилита для обогащения данных товара корзины
 *
 * ✅ ПРЕИМУЩЕСТВА:
 * - Единая точка расчета данных товара
 * - Соблюдение DRY - логика не дублируется
 * - Легко тестировать
 * - Легко переиспользовать
 */

import type { CartItem, CartImg } from '@entities/order'
import type { Addon, ExcludeIngredient } from '@entities/product'
import { findAddonsByIds, findExcludesByIds } from '@features/basket'

/**
 * Обогащенные данные товара для отображения в UI
 * ✅ Полное определение интерфейса для избежания проблем с union типами
 */
export interface EnrichedCartItem {
  // Базовые поля товара
  id: string
  name: string
  price: number
  quantity: number
  img: string[] | CartImg[]
  branch_id?: number
  category_id?: string
  addons?: number[]
  excludes?: number[]
  size?: string
  stop_list?: boolean
  isGift?: boolean // Подарок по промокоду gift_product

  // Поля для разных типов товаров
  itemType?: 'product' | 'combo'
  productId?: string
  comboId?: string
  comboSelections?: any
  combo_id?: number
  combo_selected_items?: any
  base_price?: number
  discount_percent?: number

  // Найденные добавки с полной информацией
  selectedAddons: Array<{
    id: number
    name: string
    price: number
  }>

  // Найденные исключения с полной информацией
  selectedExcludes: Array<{
    id: number
    name: string
  }>

  // Цена за единицу товара (уже включает добавки)
  pricePerItem: number

  // Итоговая цена = pricePerItem × quantity
  totalPrice: number

  // Изображение плейсхолдера (если нет своего)
  placeholderImg?: string
}

/**
 * Обогащает данные товара корзины дополнительной информацией
 *
 * @param item - товар из корзины
 * @param availableAddons - список доступных добавок из store
 * @param availableExcludes - список доступных исключений из store
 * @param placeholderImg - дефолтное изображение
 * @param allProducts - все продукты из store для синхронизации stop_list
 * @returns обогащенный товар с вычисленными данными
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- обогащение позиции корзины (addons, stop_list, изображения)
export function enrichCartItem(
  item: CartItem,
  availableAddons: Addon[],
  availableExcludes: ExcludeIngredient[],
  placeholderImg?: string,
  allProducts?: any[],
): EnrichedCartItem {
  // ✅ Защита от undefined/null
  if (!item) {
    consola.warn('[enrichCartItem] Item is undefined/null:', item)
    return {
      id: 'unknown',
      name: 'Неизвестный товар',
      price: 0,
      quantity: 1,
      img: [],
      selectedAddons: [],
      selectedExcludes: [],
      pricePerItem: 0,
      totalPrice: 0,
      stop_list: false,
    }
  }

  // Находим полные данные добавок и исключений
  const selectedAddons = findAddonsByIds(item.addons || [], availableAddons as any)
  const selectedExcludes = findExcludesByIds(item.excludes || [], availableExcludes as any)

  // item.price уже содержит базовую цену + добавки (добавляется в MoreAboutDish)
  const pricePerItem = item.price || 0

  // Итоговая цена = цена за единицу × количество
  const totalPrice = Math.round(pricePerItem * (item.quantity || 1))

  // ✅ СИНХРОНИЗАЦИЯ STOP_LIST и КАРТИНОК: Проверяем актуальный статус товара и берем img из store, если нужно
  let actualStopList = item.stop_list || false
  let actualImg: any[] | undefined = Array.isArray(item.img) ? item.img : undefined

  if (allProducts && allProducts.length > 0) {
    const productId = (item as any).productId || item.id

    // Ищем товар во всех категориях
    const actualProduct = allProducts.find((p: any) => {
      // Проверяем как обычный товар
      if (String(p.id) === String(productId)) {
        return true
      }
      // Проверяем как группу товаров
      if (p.variants && Array.isArray(p.variants)) {
        return p.variants.some((v: any) => String(v.id) === String(productId))
      }
      return false
    })

    if (actualProduct) {
      // Если это группа, ищем конкретный вариант
      if (actualProduct.variants && Array.isArray(actualProduct.variants)) {
        const variant = actualProduct.variants.find((v: any) => String(v.id) === String(productId))
        if (variant) {
          actualStopList = variant.stop_list || false
          // Если с сервера не пришли картинки для элемента корзины, подставляем картинки варианта
          if (!actualImg || actualImg.length === 0) {
            actualImg = Array.isArray(variant.img) ? variant.img : actualImg
          }
        }
      } else {
        actualStopList = actualProduct.stop_list || false
        // Если с сервера не пришли картинки для элемента корзины, подставляем картинки товара
        if (!actualImg || actualImg.length === 0) {
          actualImg = Array.isArray(actualProduct.img) ? actualProduct.img : actualImg
        }
      }
    } else {
      // Товара НЕТ в актуальном меню филиала: снят с продажи (is_active=false), удалён
      // или не привязан к филиалу (например, остался в корзине после смены филиала).
      // Касса такой заказ отклонит (PRODUCT_UNAVAILABLE) — помечаем позицию недоступной,
      // чтобы корзина показала предупреждение и предложила удалить товар.
      actualStopList = true
    }
  }

  return {
    ...item,
    // Если у элемента корзины нет своих картинок, используем актуальные из store
    img: actualImg || (Array.isArray(item.img) ? item.img : []),
    selectedAddons: selectedAddons.map(addon => ({
      id: Number(addon.id),
      name: addon.name || 'Неизвестная добавка',
      price: typeof addon.price === 'string' ? Number.parseFloat(addon.price) : addon.price,
    })),
    selectedExcludes: selectedExcludes.map(exclude => ({
      id: Number(exclude.id),
      name: exclude.name || 'Неизвестное исключение',
    })),
    pricePerItem,
    totalPrice,
    placeholderImg,
    stop_list: actualStopList, // ✅ Актуальный статус из store
  }
}

/**
 * Обогащает массив товаров корзины
 *
 * @param items - товары из корзины
 * @param availableAddons - список доступных добавок из store
 * @param availableExcludes - список доступных исключений из store
 * @param placeholderImg - дефолтное изображение
 * @param allProducts - все продукты из store для синхронизации stop_list
 * @returns массив обогащенных товаров
 */
export function enrichCartItems(
  items: CartItem[],
  availableAddons: Addon[],
  availableExcludes: ExcludeIngredient[],
  placeholderImg?: string,
  allProducts?: any[],
): EnrichedCartItem[] {
  // ✅ Защита от undefined/null и неправильного типа данных
  if (!items || !Array.isArray(items)) {
    consola.warn('[enrichCartItems] Invalid items:', items)
    return []
  }

  return items
    .filter(item => item != null) // ✅ Фильтруем null/undefined элементы
    .map(item => enrichCartItem(item, availableAddons, availableExcludes, placeholderImg, allProducts))
}
